// s169 TASK-ACTION-FEEDBACK-1 — the executor task counter math, in ONE place.
//
// Extracted from `server/executor-task-routes.ts`, where it was inline, because the client now
// needs the same computation to render an OPTIMISTIC counter before the server answers. Two
// implementations of "how many tasks remain" is precisely the fork D-156/G7 exist to prevent —
// and G7's stated trigger is exactly this moment: the second surface to need a behaviour is the
// one that extracts it rather than copying it.
//
// Pure functions over the shaped rows. No I/O, no drizzle, no React — safe on both sides.

/** The minimum a row must carry to be counted. Both the server's TaskRow and the client's
 *  wire-shape TaskRow satisfy this structurally. */
export interface CountableTask {
  isAutoResolved: boolean;
  status: string;
  consequenceTier: string;
}

/**
 * "Resolved-out" of the active list = auto-resolved OR dismissed by the executor (§1.6 Build 2).
 * Both drop below the divider and BOTH are excluded from active counts. This predicate is the
 * reason a retired/dismissed row needs no counter special-casing anywhere else.
 */
export function isActiveTask(t: CountableTask): boolean {
  return !t.isAutoResolved && t.status !== "dismissed";
}

/** Per-stage counts for a stage's full task list (active + resolved-out). */
export function stageCounts(stageTasks: CountableTask[]): {
  total: number; done: number; autoResolved: number; dismissed: number;
} {
  const active = stageTasks.filter(isActiveTask);
  return {
    total: active.length,
    done: active.filter((t) => t.status === "done").length,
    autoResolved: stageTasks.filter((t) => t.isAutoResolved).length,
    dismissed: stageTasks.filter((t) => !t.isAutoResolved && t.status === "dismissed").length,
  };
}

/**
 * The whole-estate summary that drives all four "N remaining" display sites (D-168.12: a promoted
 * per-record row counts as one task each, which falls out of this for free — the rows are already
 * distinct by the time they reach here).
 */
export function summarizeTasks(tasks: CountableTask[]): {
  totalTasks: number; doneTasks: number; criticalRemaining: number; autoResolved: number; dismissed: number;
} {
  const active = tasks.filter(isActiveTask);
  return {
    totalTasks: active.length,
    doneTasks: active.filter((t) => t.status === "done").length,
    criticalRemaining: active.filter((t) => t.consequenceTier === "critical" && t.status !== "done").length,
    autoResolved: tasks.filter((t) => t.isAutoResolved).length,
    dismissed: tasks.filter((t) => !t.isAutoResolved && t.status === "dismissed").length,
  };
}

/**
 * EXECUTOR-GAP-PANEL-1 (s199) — the READY / BLOCKED split the planner's gap panel shows.
 *
 * 🔴 Two numbers, and the separation is the ruling (SPEC s199, "Two numbers, not one"). The task
 * UNIVERSE does not move when a planner closes a gap — the executor still has the same job, it just
 * got easier — so a single "N tasks left" number would have had to lie in one direction or the
 * other. `ready` is the number the planner's effort actually moves.
 *
 * It lives HERE, beside `summarizeTasks`, for the reason that function was extracted in the first
 * place (s169/G7): the second surface to need a count is the one that puts it in the shared module,
 * not the one that inlines it in a component. The server emits the numbers with this function and
 * the client recomputes optimistically with the SAME call after a save, so the counter cannot drift
 * mid-interaction — which is the whole of ship-gate 3 ("the counter never lies").
 *
 * The derivation is exactly what the engine already emits and nothing more: `PlannerGap.taskKeys`
 * names every task consuming a gapped field, so a task is BLOCKED iff it appears in the union of
 * those key lists. Deliberately many-to-many in both directions — a task can be blocked by several
 * fields and a field can block several tasks — which is why closing one gap sometimes frees a task
 * and sometimes frees none. That unevenness is true, not a rounding.
 *
 * Callers pass the CLIENT's task universe (the active templates minus the ones this estate's own
 * planner facts definitively rule out), never a literal.
 */
export interface ReadyBlockedCounts {
  /** Tasks in this estate's universe — the number that does NOT move as gaps close. */
  total: number;
  /** Tasks nothing on the gap list is blocking. */
  ready: number;
  /** Tasks blocked by at least one open gap. `ready + blocked === total`. */
  blocked: number;
}

/** One open gap, reduced to the only thing this computation reads. `PlannerGap` satisfies it. */
export interface BlockingGap {
  taskKeys: readonly string[];
}

export function readyBlockedCounts(
  taskKeyUniverse: readonly string[],
  openGaps: readonly BlockingGap[],
): ReadyBlockedCounts {
  const universe = new Set(taskKeyUniverse);
  const blockedKeys = new Set<string>();
  for (const gap of openGaps) {
    for (const key of gap.taskKeys) {
      // A gap may name a task this estate has gated out; it is not part of the universe, so it can
      // neither be ready nor blocked. Intersecting here (rather than trusting the gap list) is what
      // keeps `ready + blocked === total` true for every client.
      if (universe.has(key)) blockedKeys.add(key);
    }
  }
  const blocked = blockedKeys.size;
  return { total: universe.size, ready: universe.size - blocked, blocked };
}
